// Audience-aware landing copy. The product serves two buyers — outside counsel at
// law firms and in-house regulatory / government-affairs teams at operators, carriers,
// trade associations, and consultancies. The public "/" landing uses the `neutral`
// voice so it speaks to both; `/business` uses the operator voice; `attorney` is used
// for the logged-in experience when the signup domain looks like a law firm.

export type Audience = 'neutral' | 'attorney' | 'business';

export interface LandingCopy {
  /** Sub-headline under the rotating hero H1. */
  heroSubhead: string;
  /** Caption under the "2–3 days → 3 min" stat. */
  statBandCaption: string;
  /** "Why CommsGenie" section heading. */
  painHeading: string;
  /** Final bullet of the "Today, by hand" list — the payoff-that-slips line. */
  painLastStep: string;
  /** Final bullet of the "With CommsGenie" list — the payoff-delivered line. */
  solutionLastStep: string;
  /** Caption on the .docx export output card. */
  outputDocxCaption: string;
  /** Sub-headline on the logged-in dashboard. */
  dashboardSubhead: string;
}

export const LANDING_COPY: Record<Audience, LandingCopy> = {
  neutral: {
    heroSubhead:
      'Turn hundreds of filings on an FCC docket into a shareable, page-cited summary — in 3 minutes.',
    statBandCaption:
      'Start your review from a smarter starting point — get to a finished briefing faster.',
    painHeading: 'The painful parts of FCC docket work, automated.',
    painLastStep: 'The briefing you owe is two to three days late.',
    solutionLastStep: 'The briefing lands in your inbox before anyone has to ask.',
    outputDocxCaption: '.docx export — drop into your memo or briefing unchanged',
    dashboardSubhead:
      'Search any FCC proceeding, get AI-generated comment summaries, and track the activity that matters to you.',
  },
  attorney: {
    heroSubhead:
      'Turn hundreds of filings on an FCC docket into a filing-ready, page-cited summary — in 3 minutes.',
    statBandCaption:
      'Start your manual review from a smarter starting point — get to client memos faster.',
    painHeading: 'The painful parts of FCC practice, automated.',
    painLastStep: 'Memo the partner asked for is two to three days late.',
    solutionLastStep: 'Memo lands in your inbox before the partner thinks to ask.',
    outputDocxCaption: '.docx export — drop into a partner memo unchanged',
    dashboardSubhead:
      'Search any FCC proceeding, get AI-generated comment summaries, and track the activity that matters to your clients.',
  },
  business: {
    heroSubhead:
      'Turn hundreds of filings on an FCC docket into a shareable, page-cited briefing your team can act on — in 3 minutes.',
    statBandCaption:
      'Start from a smarter starting point — brief your team and leadership faster.',
    painHeading: 'The painful parts of tracking FCC dockets, automated.',
    painLastStep: 'The update your leadership asked for is two to three days late.',
    solutionLastStep: 'The briefing lands in your inbox before leadership asks.',
    outputDocxCaption: '.docx export — drop into a board deck or client update unchanged',
    dashboardSubhead:
      'Search any FCC proceeding, get AI-generated comment summaries, and track the activity that matters to your business.',
  },
};

// Rough firm-vs-general classifier used to adapt the logged-in experience from the
// signup email domain. Personal/unknown domains fall back to neutral.
const FIRM_DOMAIN_HINTS = ['law', 'legal', 'llp', 'attorney', 'counsel', 'esq'];
const PERSONAL_DOMAINS = new Set([
  'gmail.com', 'outlook.com', 'hotmail.com', 'yahoo.com', 'icloud.com', 'proton.me', 'protonmail.com',
]);

export function inferAudienceFromEmail(email?: string | null): Audience {
  if (!email) return 'neutral';
  const domain = email.split('@')[1]?.toLowerCase().trim() ?? '';
  if (!domain || PERSONAL_DOMAINS.has(domain)) return 'neutral';
  const name = domain.split('.')[0] ?? domain;
  if (FIRM_DOMAIN_HINTS.some((h) => name.includes(h))) return 'attorney';
  return 'business';
}
